import React, { useState, useEffect } from 'react';
import { Button, Container, Divider, Grid, Typography } from '@mui/material';
import useStore from 'src/store/cns';
import { formatEther } from 'ethers';
import { ACTIVE_DEPLOYMENT } from 'src/utils/contracts/common';
import RegistrarDeployment from '../../contracts/deployments/Registrar.json';
import SelectToken from '../widget/SelectToken';
import { checkAllowance } from 'src/utils/contracts/token';
import { getProvider } from 'src/utils/wallet';
import { balanceOf } from 'src/utils/contracts/token';
import Allowance from '../payment/Allowance';
import Registration from '../payment/Registration';
import Cost from '../payment/Cost';

const Register: React.FC<{
  setRegisterState: (state: RegisterState) => void;
  walletAddress: string;
  domainName: string;
}> = ({ setRegisterState, walletAddress, domainName }) => {
  const tokens = useStore(state => state.tokens);

  const [selectedToken, setSelectedToken] = useState<Token>(tokens[0]);
  const [registrationPeriod, setRegistrationPeriod] = useState(1);
  const [registrationCost, setRegistrationCost] = useState(
    Number(selectedToken.basePrice),
  );
  const [allowance, setAllowance] = useState(0);
  const [balance, setBalance] = useState('0');

  const handleChange = (token: Token) => {
    setSelectedToken(token);
  };

  const updateCostAndYears = (cost: number, years: number) => {
    setRegistrationCost(cost);
    setRegistrationPeriod(years);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (
        selectedToken.tokenAddress !==
        '0x0000000000000000000000000000000000000000'
      ) {
        const newBalance = await balanceOf(selectedToken.tokenAddress);
        setBalance(formatEther(newBalance));
        setAllowance(
          await checkAllowance(
            RegistrarDeployment[ACTIVE_DEPLOYMENT].address,
            selectedToken.tokenAddress,
          ),
        );
      } else if (
        selectedToken.tokenAddress ===
        '0x0000000000000000000000000000000000000000'
      ) {
        const provider = getProvider();
        const newBalance = await provider.getBalance(walletAddress);
        setBalance(formatEther(newBalance));
      }
    };
    fetchData();
  }, [selectedToken]);

  return (
    <Container
      data-cy="register-domain"
      maxWidth="sm"
      sx={{
        maxHeight: '90%',
        overflow: 'auto',
        border: '1px solid',
        borderColor: 'grey.600',
        borderRadius: '8px',
        paddingX: '50px',
        paddingY: '24px',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        You are about to register: {domainName}.cam
      </Typography>
      <Grid container rowSpacing={4} alignItems="center">
        <Grid item xs={7}>
          <Typography>Expiration Date</Typography>
        </Grid>
        <Grid item xs={5}>
          <Cost
            onValueChange={updateCostAndYears}
            expirationDate={new Date().toISOString().split('T')[0]}
            token={selectedToken}
          />
        </Grid>
        <Grid item xs={7}>
          <Typography>Registration Token</Typography>
        </Grid>
        <Grid item xs={5} textAlign="right">
          <SelectToken onValueChange={handleChange} />
        </Grid>
        <Grid item xs={8}>
          <Typography>Current Balance</Typography>
        </Grid>
        <Grid item xs={4} textAlign="right">
          {balance} {selectedToken.symbol}
        </Grid>
        <Grid item xs={8}>
          <Typography>Registration Cost</Typography>
        </Grid>
        <Grid item xs={4} textAlign="right" data-cy="registration-base-price">
          <Typography>
            - {registrationCost} {selectedToken.symbol}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {Number(balance) <= registrationCost ? (
          <Grid item xs={12} textAlign="center">
            <Typography>
              Not enough {selectedToken.name} to purchase the domain.
            </Typography>
          </Grid>
        ) : (
          <>
            <Grid item xs={8}>
              <Typography>After Purchase Balance</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              {Number(balance) - registrationCost} {selectedToken.symbol}
            </Grid>

            <Grid item xs={12} textAlign="center" sx={{ mt: 2 }}>
              {allowance < registrationCost &&
              selectedToken.tokenAddress !==
                '0x0000000000000000000000000000000000000000' ? (
                <Allowance
                  cost={registrationCost}
                  token={selectedToken}
                  onValueChange={setAllowance}
                />
              ) : (
                <Registration
                  setRegisterState={setRegisterState}
                  domainName={domainName}
                  registrationPeriod={registrationPeriod}
                  walletAddress={walletAddress}
                  registrationCost={registrationCost}
                  tokenAddress={selectedToken.tokenAddress}
                />
              )}
            </Grid>

            {selectedToken.tokenAddress !==
              '0x0000000000000000000000000000000000000000' &&
            allowance < registrationCost ? (
              <>
                <Grid item xs={12} textAlign="center">
                  <Typography variant="caption">
                    You need to approve the Registrar to spend ERC-20 tokens on
                    your behalf.
                  </Typography>
                </Grid>
              </>
            ) : null}

            <Grid item xs={12} textAlign="center" sx={{ mt: 2 }}>
              <Button
                sx={{
                  color: 'text.primary',
                  borderColor: 'grey.600',
                  '&:hover': {
                    backgroundColor: 'grey.800',
                    borderColor: 'grey.600',
                  },
                }}
                variant="text"
                onClick={() =>
                  setRegisterState({
                    domainName: '',
                    status: 'searching',
                  })
                }
              >
                Cancel
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </Container>
  );
};

export default React.memo(Register);
